import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function daysAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  const now = new Date();
  const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff}d ago`;
}

function getRunsColor(runs) {
  if (runs === 0) return "text-green-400";
  if (runs <= 2) return "text-green-300";
  if (runs <= 3) return "text-white";
  return "text-red-400";
}

function getDecisionBadge(decision) {
  if (!decision) return null;
  const colors = {
    "W": "bg-green-500/20 text-green-400",
    "L": "bg-red-500/20 text-red-400",
    "SV": "bg-blue-500/20 text-blue-400",
    "H": "bg-astros-orange/20 text-astros-orange",
  };
  return (
    <span className={`text-xs font-bold px-1.5 py-0.5 ${colors[decision] || "text-astros-muted"}`}>
      {decision}
    </span>
  );
}

export default function PitcherOutingsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/pitchers/last-outing`);
        setData(res.data);
      } catch (e) {
        console.error("Error fetching pitcher outings:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const outings = data?.outings || [];
  const filtered = filter === "all" ? outings
    : filter === "starters" ? outings.filter(o => o.is_starter)
    : outings.filter(o => !o.is_starter);

  return (
    <div data-testid="pitcher-outings-tab">
      <div className="flex items-center gap-3 mb-2">
        <Calendar className="w-6 h-6 text-astros-orange" />
        <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white">LAST PITCHER OUTINGS</h2>
      </div>
      <p className="text-astros-muted text-sm mb-6">
        Most recent appearance for each Astros pitcher, sorted by date.
      </p>

      <div className="flex gap-3 mb-6">
        {[
          { key: "all", label: "ALL" },
          { key: "starters", label: "STARTERS" },
          { key: "relievers", label: "RELIEVERS" },
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => setFilter(opt.key)}
            className={`px-4 py-2 font-bebas text-base tracking-wide transition-colors ${
              filter === opt.key
                ? "text-astros-orange border-b-2 border-astros-orange"
                : "text-astros-muted hover:text-white"
            }`}
            data-testid={`outings-filter-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="outings-table">
          <thead>
            <tr className="border-b border-astros-border text-astros-muted text-xs uppercase tracking-wider">
              <th className="text-left py-3 pr-2 w-8"></th>
              <th className="text-left py-3 pr-4 font-medium">Pitcher</th>
              <th className="text-center py-3 px-2 font-medium">Date</th>
              <th className="text-left py-3 px-2 font-medium">vs</th>
              <th className="text-center py-3 px-2 font-medium">IP</th>
              <th className="text-center py-3 px-2 font-medium">Pitches</th>
              <th className="text-center py-3 px-2 font-medium">R</th>
              <th className="text-center py-3 px-2 font-medium">ER</th>
              <th className="text-center py-3 px-2 font-medium">H</th>
              <th className="text-center py-3 px-2 font-medium">K</th>
              <th className="text-center py-3 px-2 font-medium">BB</th>
              <th className="text-center py-3 px-2 font-medium hidden sm:table-cell">Dec</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((outing, index) => (
              <motion.tr
                key={outing.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className="border-b border-astros-border/30 hover:bg-astros-border/20 cursor-pointer transition-colors"
                onClick={() => navigate(`/player/${outing.id}`)}
                data-testid={`outing-${outing.id}`}
              >
                <td className="py-3 pr-2">
                  <img
                    src={outing.photo_url}
                    alt={outing.name}
                    className="w-8 h-8 object-cover bg-astros-navy"
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=32&h=32&fit=crop"; }}
                  />
                </td>
                <td className="py-3 pr-4">
                  <div className="flex flex-col">
                    <span className="text-white font-semibold text-sm">{outing.name}</span>
                    <div className="flex items-center gap-2 text-xs text-astros-muted">
                      <span className="text-astros-orange font-bold">#{outing.jersey_number || "00"}</span>
                      <span className={outing.is_starter ? "text-sky-400" : "text-amber-400"}>
                        {outing.is_starter ? "SP" : "RP"}
                      </span>
                      <span className="text-astros-muted/50">ERA {outing.season_era}</span>
                    </div>
                  </div>
                </td>
                <td className="text-center py-3 px-2">
                  <div className="flex flex-col items-center">
                    <span className="text-white text-xs font-semibold">{formatDate(outing.date)}</span>
                    <span className="text-astros-muted text-[10px]">{daysAgo(outing.date)}</span>
                  </div>
                </td>
                <td className="text-left py-3 px-2 text-xs text-white">
                  <span className="text-astros-muted">{outing.is_home ? "vs" : "@"} </span>
                  {outing.opponent}
                </td>
                <td className="text-center py-3 px-2 text-white font-semibold">{outing.innings_pitched}</td>
                <td className="text-center py-3 px-2 text-white font-semibold">{outing.pitches}</td>
                <td className={`text-center py-3 px-2 font-bold ${getRunsColor(outing.runs)}`}>{outing.runs}</td>
                <td className={`text-center py-3 px-2 font-bold ${getRunsColor(outing.earned_runs)}`}>{outing.earned_runs}</td>
                <td className="text-center py-3 px-2 text-white">{outing.hits}</td>
                <td className="text-center py-3 px-2 text-white">{outing.strikeouts}</td>
                <td className="text-center py-3 px-2 text-white">{outing.walks}</td>
                <td className="text-center py-3 px-2 hidden sm:table-cell">{getDecisionBadge(outing.decision)}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <p className="text-astros-muted text-center py-8">No pitcher outings found</p>
      )}
    </div>
  );
}
